import { useCallback, useEffect, useState } from "react";
import useApiRequest from "../../../lib/hooks/useRequest";

/** Geplant steht zwischen Entwurf und veroeffentlicht: fertig, aber noch nicht sichtbar (A11). */
export type NeuigkeitStand = "entwurf" | "geplant" | "veroeffentlicht";
export type NeuigkeitEinordnung = "neu" | "verbessert" | "behoben" | "erinnerung" | "wissenswert";

/** Das Video einer Neuigkeit, fertig zum Anzeigen (A12). */
export interface NeuigkeitVideo {
  provider: "youtube" | "vimeo" | "loom";
  /** Laedt erst, wenn der Kunde auf Abspielen klickt. */
  embedUrl: string;
  title: string;
  /** Wie der Anbieter das Video nennt - bleibt neben einem eigenen Satz stehen (KAN-293). */
  providerTitle: string | null;
  duration: string | null;
  /** Liegt in unserem Ablageort, nicht beim Anbieter. */
  thumbnailUrl: string | null;
  /** Der Link, wie er eingefuegt wurde — nur die Verwaltung bekommt ihn (KAN-291). */
  url: string;
}

/** Beim Bearbeiten steht hier der rohe Ziel-Schluessel, nicht der aufgeloeste Pfad. */
export interface NeuigkeitAktion {
  label: string;
  kind: "intern" | "extern";
  target: string;
}

export interface Neuigkeit {
  id: string;
  key: string;
  title: string;
  problemText: string;
  solutionText: string;
  group: NeuigkeitEinordnung;
  date: string;
  opensDialog: boolean;
  status: NeuigkeitStand;
  actions: NeuigkeitAktion[];
  imageUrl: string | null;
  publishedAt: string | null;
  /** Nur bei "geplant" gesetzt: wann der Eintrag von selbst sichtbar wird (A11). */
  publishAt: string | null;
  video: NeuigkeitVideo | null;
  /** Der Link, wie er eingefuegt wurde — nur in der Verwaltung. Bestand, siehe `videos`. */
  videoUrl: string | null;
  /** Alle Videos, in Reihenfolge, je mit dem eingefuegten Link (KAN-291). */
  videos: NeuigkeitVideo[];
  /** Der Stand, von dem aus bearbeitet wird; geht beim Speichern zurueck (KAN-286). */
  updatedAt: string | null;
  /** Wer den Eintrag geschrieben hat (KAN-297); die Erstbefuellung hat niemanden. */
  author: string | null;
  /** Was er bewirkt hat (KAN-296). Fehlt, solange nichts veroeffentlicht ist. */
  zahlen: NeuigkeitZahlen | null;
}

/** Ein Knopf oder ein Video mit seiner Zahl (KAN-296). */
export interface NeuigkeitStelle {
  stelle: number;
  label: string;
  personen: number;
}

/**
 * Was eine Neuigkeit bewirkt hat (KAN-296). Jede Zahl zaehlt Personen, nicht Klicks.
 *
 * Nur Rohwerte: Gerechnet wird dort, wo gezeigt wird, und zwar durchgehend gegen dieselbe
 * Grundgesamtheit. Zwei verschiedene Nenner in gleich aussehenden Kacheln kann niemand raten.
 */
export interface NeuigkeitZahlen {
  erreicht: number;
  /** Wie viele Personen den Eintrag haetten sehen koennen - ohne das sagt `erreicht` nichts. */
  moeglich: number;
  geoeffnet: number;
  betriebe: number;
  /** Wie viele Betriebe es insgesamt gibt - sonst sagt `betriebe` nichts. */
  betriebeGesamt: number;
  knoepfe: NeuigkeitStelle[];
  videos: NeuigkeitStelle[];
}

/** Eine Seite, auf die ein Knopf nach innen zeigen darf — kommt vom Server (A7). */
export interface NeuigkeitZiel {
  key: string;
  label: string;
  path: string;
}

/** Wie ein Stand heisst und aussieht - an beiden Stellen gleich, Liste wie Schreibmaske. */
export const STAND_LABEL: Record<NeuigkeitStand, string> = {
  entwurf: "Entwurf",
  geplant: "Geplant",
  veroeffentlicht: "Veröffentlicht",
};

export const STAND_PLAKETTE: Record<NeuigkeitStand, string> = {
  entwurf: "bg-gray-100 text-gray-700",
  geplant: "bg-sky-50 text-sky-800",
  veroeffentlicht: "bg-green-50 text-green-800",
};

/**
 * Was beim Anlegen oder Aendern an den Server geht.
 *
 * Ohne `date`: Das angezeigte Datum bildet der Server selbst aus dem Erscheinen. Ein zweites
 * Feld dafuer koennte davon abweichen, und genau das ist frueher passiert (KAN-281).
 */
export type NeuigkeitEingabe = Partial<Pick<Neuigkeit, "title" | "problemText" | "solutionText" | "group" | "opensDialog" | "actions">> & {
  /** Leerer String entfernt das Video; gar nicht mitschicken laesst es unberuehrt (A12). */
  /** Die Videos, in Reihenfolge. Nicht mitgeschickt heisst unveraendert (KAN-291). */
  videos?: { url: string; title?: string }[];
  /** Der Stand beim Oeffnen. Hat inzwischen jemand anderes gespeichert, sagt der Server ab. */
  basedOn?: string;
};

const BASIS = "admin/feature-hints";

/**
 * Die Datengrundlage der Neuigkeiten-Verwaltung (KAN-276).
 *
 * Jeder schreibende Weg laedt die Liste danach frisch, statt den Stand lokal
 * nachzuhalten: Veroeffentlichen und Zurueckziehen aendern mehr als ein Feld, und ein
 * halb nachgezogener Stand in der Oberflaeche ist schlimmer als ein zweiter Abruf.
 */
export const useAdminNeuigkeiten = () => {
  const { apiRequest, apiFileRequest } = useApiRequest();
  const [neuigkeiten, setNeuigkeiten] = useState<Neuigkeit[]>([]);
  const [ziele, setZiele] = useState<NeuigkeitZiel[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true);
    const antwort = await apiRequest<Neuigkeit[]>(BASIS, "GET");
    // Nur etwas, das wirklich wie eine Liste aussieht, wird eine. Scheitert das Lesen der
    // Antwort, reicht useRequest den Platzhalter "success" durch - und der stand frueher
    // als leerer Bereich auf dem Schirm.
    if (Array.isArray(antwort.data)) {
      setNeuigkeiten(antwort.data);
      setFehler(false);
    } else {
      setFehler(true);
    }
    setLaedt(false);
  }, [apiRequest]);

  const zieleLaden = useCallback(async () => {
    const antwort = await apiRequest<NeuigkeitZiel[]>(`${BASIS}/ziele`, "GET");
    if (Array.isArray(antwort.data)) setZiele(antwort.data);
  }, [apiRequest]);

  useEffect(() => {
    laden();
    zieleLaden();
  }, [laden, zieleLaden]);

  const erfolgreich = (status: number, fehlertext: unknown): boolean => !fehlertext && status >= 200 && status < 300;

  const anlegen = useCallback(
    async (eingabe: NeuigkeitEingabe): Promise<Neuigkeit | null> => {
      const antwort = await apiRequest<Neuigkeit>(BASIS, "POST", { body: eingabe });
      if (!erfolgreich(antwort.status, antwort.error) || !antwort.data?.id) return null;
      await laden();
      return antwort.data;
    },
    [apiRequest, laden]
  );

  /**
   * Ein einzelner Eintrag (KAN-289). Die Schreibmaske wartete beim Oeffnen sonst darauf,
   * dass die ganze Liste da ist - nur um darin ihren einen Eintrag zu suchen.
   */
  const einzelnLaden = useCallback(
    async (id: string): Promise<Neuigkeit | null> => {
      const antwort = await apiRequest<Neuigkeit>(`${BASIS}/${id}`, "GET");
      if (!erfolgreich(antwort.status, antwort.error) || !antwort.data?.id) return null;
      return antwort.data;
    },
    [apiRequest]
  );

  /**
   * Loest einen Video-Link auf, ohne zu speichern (KAN-292). Der Server weist einen fremden
   * Anbieter dabei schon ab - die Maske erfaehrt es also sofort, nicht erst beim Absenden.
   */
  const vorschauHolen = useCallback(
    async (url: string): Promise<NeuigkeitVideo | null> => {
      // Ohne Fehler-Fenster: Ein Link, der dem Server nicht passt, gehoert unter das Feld -
      // nicht ueber die ganze Seite (KAN-292).
      const antwort = await apiRequest<Omit<NeuigkeitVideo, "url">>(`${BASIS}/video-vorschau`, "POST", {
        body: { url },
        withoutErrorModal: true,
      });
      if (!erfolgreich(antwort.status, antwort.error) || !antwort.data) return null;
      return { ...antwort.data, url };
    },
    [apiRequest]
  );

  const aendern = useCallback(
    async (id: string, eingabe: NeuigkeitEingabe): Promise<{ geklappt: boolean; konflikt: boolean }> => {
      const antwort = await apiRequest<Neuigkeit>(`${BASIS}/${id}`, "PATCH", { body: eingabe });
      const geklappt = erfolgreich(antwort.status, antwort.error);
      if (geklappt) await laden();
      // 409 heisst hier genau eine Sache: Jemand anderes war schneller (KAN-286).
      return { geklappt, konflikt: antwort.status === 409 };
    },
    [apiRequest, laden]
  );

  // Ohne Zeitpunkt sofort - das ist der haeufige Fall und braucht deshalb kein Zutun (A11).
  const veroeffentlichen = useCallback(
    async (id: string, zeitpunkt?: string): Promise<boolean> => {
      const antwort = await apiRequest<Neuigkeit>(`${BASIS}/${id}/publish`, "POST", {
        body: zeitpunkt ? { publishAt: zeitpunkt } : {},
      });
      const geklappt = erfolgreich(antwort.status, antwort.error);
      if (geklappt) await laden();
      return geklappt;
    },
    [apiRequest, laden]
  );

  const zurueckziehen = useCallback(
    async (id: string): Promise<boolean> => {
      const antwort = await apiRequest<Neuigkeit>(`${BASIS}/${id}/unpublish`, "POST");
      const geklappt = erfolgreich(antwort.status, antwort.error);
      if (geklappt) await laden();
      return geklappt;
    },
    [apiRequest, laden]
  );

  const loeschen = useCallback(
    async (id: string): Promise<boolean> => {
      const antwort = await apiRequest<void>(`${BASIS}/${id}`, "DELETE");
      const geklappt = erfolgreich(antwort.status, antwort.error);
      if (geklappt) await laden();
      return geklappt;
    },
    [apiRequest, laden]
  );

  const bildHochladen = useCallback(
    async (id: string, datei: File): Promise<boolean> => {
      const antwort = await apiFileRequest<Neuigkeit>(`${BASIS}/${id}/image`, "POST", datei);
      const geklappt = erfolgreich(antwort.status, antwort.error);
      if (geklappt) await laden();
      return geklappt;
    },
    [apiFileRequest, laden]
  );

  const bildEntfernen = useCallback(
    async (id: string): Promise<boolean> => {
      const antwort = await apiRequest<Neuigkeit>(`${BASIS}/${id}/image`, "DELETE");
      const geklappt = erfolgreich(antwort.status, antwort.error);
      if (geklappt) await laden();
      return geklappt;
    },
    [apiRequest, laden]
  );

  return {
    neuigkeiten,
    ziele,
    laedt,
    fehler,
    laden,
    einzelnLaden,
    anlegen,
    aendern,
    vorschauHolen,
    veroeffentlichen,
    zurueckziehen,
    loeschen,
    bildHochladen,
    bildEntfernen,
  };
};
